<script>
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("LegalCreator.vue");
</script>

<script setup>
import VCreator from "@/shared/views/vCreator.vue";
import cCreatorStep from "@/shared/components/creator/cCreatorStep.vue";
import cCreatorThemes from "@/shared/components/creator/cCreatorThemes.vue";
import { ButtonPrimarySm } from "@easy/ui/buttons";
import { RocketIcon } from "@easy/ui/icons";
import { useLegalCreatorStore } from "@/apps/legal/project/onboarding/legal-creator.store";
import { legalOnboardingService } from "@/apps/legal/project/onboarding/legal-onboarding.service";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { isColorDark } from "@/shared/utils/helpers";
import { useOnboardingVideo } from "@/shared/projects/onboarding/useOnboardingVideos";
import { useAsyncState } from "@vueuse/core";
import { userService } from "@/shared/user/user.service";
import { linksUtil } from "@/shared/utils/router/links";

const { locale } = useI18n();
const { video } = useOnboardingVideo("legal");
const legalCreatorStore = useLegalCreatorStore();
const { onboardState, previewCssVars, previewComponent } = storeToRefs(legalCreatorStore);
const { toggleCreatorShown, onboardingAction } = legalCreatorStore;

const backgroundColor = computed(() =>
    isColorDark(onboardState.value?.theme?.colors?.background || "") ? "#f9fafb" : "#17171C"
);

const generateOnboarding = onboardingAction(() => {
    logger.debug("send LEGAL onboarding...", locale.value, onboardState.value);
    return legalOnboardingService.sendOnboardData({
        ...onboardState.value,
        theme: onboardState.value.theme,
        lang: locale.value,
    });
});

// Collect tenant data:
useAsyncState(
    () => userService.getTenantDetails(),
    {},
    {
        immediate: true,
        onSuccess(data) {
            onboardState.value.tenantData = data || {};
            logger.debug("onboardState.value.tenantData", onboardState.value.tenantData);
            if (onboardState.value.tenantData.websiteUrl) {
                onboardState.value.tenantData.websiteUrl = linksUtil.trimHttp(data.websiteUrl);
            }
        },
    }
);
</script>

<template>
    <VCreator
        :backColor="backgroundColor"
        :canSkipGenerator="!onboardState.isLoading"
        :description="$t('legal.creator.subtitle')"
        :title="$t('legal.creator.title')"
        :video="video"
        @close="toggleCreatorShown(false)"
    >
        <cCreatorStep
            :title="$t('legal.creator.step_theme_title')"
            :description="$t('legal.creator.step_theme_description')"
            :step="1"
        >
            <cCreatorThemes v-model="onboardState.theme" />
        </cCreatorStep>
        <cCreatorStep
            :title="$t('legal.creator.step_final_title')"
            :description="$t('legal.creator.step_final_description')"
            :step="2"
            :last="true"
        >
            <ButtonPrimarySm @click="generateOnboarding()" :loading="onboardState.isLoading">
                <RocketIcon />
                <span>{{ $t("legal.creator.step_final_cta") }} </span>
            </ButtonPrimarySm>
        </cCreatorStep>
        <template #preview>
            <div :style="{ ...previewCssVars }">
                <component :is="previewComponent" />
            </div>
        </template>
    </VCreator>
</template>
